import React, { LegacyRef, useContext, useEffect, useState } from 'react';
import Tippy from '@tippyjs/react/headless';
import { Placement } from '@popperjs/core';
import { Hint } from 'src/features/common/types/application/pending-app';
import { Row } from 'src/common';
import { PAGE_NAME } from '../../types/tealium-service';
import './error-code-tooltip.scss';
import { ApplicationContext } from 'src/features/onboarding/context';
import { IOnboardingTealiumService } from 'src/services/tealium/onboarding/onboarding-tealium-service';

type ToolTipProps = {
  children: any;
  content: Hint[];
  showOnClick?: boolean;
  orientation?: Placement;
  isMobile?: boolean;
  lightMode?: boolean;
  interactive?: boolean;
  isUpload?: boolean;
  isComboField?: boolean;
  showErrorCodes?: boolean;
  pageName: PAGE_NAME;
};

type HintNameProp = {
  name: string;
  pageName: PAGE_NAME;
  onboardingTealiumService: IOnboardingTealiumService;
};

const EC_KEYWORDS = {
  ERROR_CODE: 'Error code',
  CONTACT_US: 'contact us on',
};

const getNumber = (text: string) => {
  const validMobileNumberRegExp = /(\+27|0)[1-8]\d\s?\d{3,4}\s?\d{3,4}/;
  const res = text.match(validMobileNumberRegExp);
  const number = res ? res[0] : '';
  return number;
};

const StyleErrorMessage = (props: HintNameProp) => {
  const tel = getNumber(props.name);
  const contactText = `${EC_KEYWORDS.CONTACT_US} ${tel}`;

  return !!tel && props.name.includes(tel) ? (
    <p className="error-message">
      {props.name.replace(contactText, '')}
      <a
        href={`tel:${tel}`}
        className="contact-info"
        onClick={() =>
          props.onboardingTealiumService.onErrorToolTipActions(
            props.name,
            'contact number',
            props.pageName,
          )
        }
      >
        {contactText}
      </a>
    </p>
  ) : (
    <p className="error-message">{props.name}</p>
  );
};

// TODO: make this a generic tooltip or create a generic tooltip
export const ErrorCodeTooltip = ({
  children,
  content,
  showOnClick = true,
  orientation = 'top',
  isMobile = false,
  lightMode = true,
  isUpload = false,
  showErrorCodes = true,
  isComboField,
  pageName,
}: ToolTipProps) => {
  const { onboardingTealiumService } = useContext(ApplicationContext);
  const [arrow, setArrow] = useState(null);
  const [visible, setVisible] = useState(false);
  const [errorIndex, setErrorIndex] = useState(0);
  const [isLast, setIsLast] = useState(false);
  const isMultipleErrors = content?.length !== 1;
  const mobileOffset = 30;
  const desktopOffset = 20;

  useEffect(() => {
    errorIndex + 1 === content?.length ? setIsLast(true) : setIsLast(false);
  }, [errorIndex]);

  const getOffset = (orientation: string, isOffsetForMobile: boolean) => {
    const offset = isOffsetForMobile ? mobileOffset : desktopOffset;

    switch (orientation) {
      case 'top':
        return [0, offset];
      case 'bottom':
        return [0, offset];
      case 'left':
        return [offset, 0];
      case 'right':
        return [offset, 0];
    }
  };

  const getTippy = (showOnClick: boolean, isMobile: boolean) => {
    const variableProps = { visible, onClickOutside: () => {} };

    if (showOnClick) {
      variableProps.visible = visible;
      variableProps.onClickOutside = () => setVisible(false);
    }

    return (
      <Tippy
        {...variableProps}
        placement={orientation}
        interactive={true}
        render={(attrs) => (
          <div
            className={`tooltip ${(isComboField && 'combo-field') || ''} ${
              (isUpload && 'upload-page') || ''
            } ${(lightMode && 'light-mode') || ''} `}
            tabIndex={-1}
            {...attrs}
          >
            <div className="tooltip-text">
              <section className="tooltip-text-header">
                {!showErrorCodes && (
                  <Row flex={1} justifyContent={'flex-end'}>
                    <span onClick={() => setVisible(false)}>X</span>
                  </Row>
                )}
                {showErrorCodes && (
                  <>
                    <p className="error-code">
                      {content[errorIndex].code.includes(EC_KEYWORDS.ERROR_CODE)
                        ? content[errorIndex].code
                        : `${EC_KEYWORDS.ERROR_CODE}: ${content[errorIndex].code}`}
                    </p>
                    <p>
                      <span className="error-index">{`${errorIndex + 1}`}</span>
                      {` of ${content?.length}`}
                    </p>
                  </>
                )}
              </section>
              <section className="tooltip-text-body">
                <StyleErrorMessage
                  name={content[errorIndex].hint}
                  pageName={pageName}
                  onboardingTealiumService={onboardingTealiumService}
                />
              </section>
              <section className="tooltip-text-footer">
                {errorIndex > 0 && (
                  <a
                    onClick={() => {
                      onboardingTealiumService.onErrorToolTipActions(
                        content[errorIndex].name,
                        'previous',
                        pageName,
                      );
                      setErrorIndex(errorIndex - 1);
                    }}
                    className="previous-button"
                  >
                    Previous
                  </a>
                )}
                {errorIndex !== 0 && ' | '}

                {isMultipleErrors && (
                  <a
                    className={`next-button ${isLast ? 'disabled' : ''}`}
                    onClick={() => {
                      if (!isLast) {
                        onboardingTealiumService.onErrorToolTipActions(
                          content[errorIndex].name,
                          'next',
                          pageName,
                        );
                        setErrorIndex(errorIndex + 1);
                      }
                    }}
                  >
                    Next
                  </a>
                )}
              </section>
            </div>
            <div
              id="arrow"
              data-popper-arrow=""
              className={`arrow ${isMobile ? 'is-mobile' : ''}`}
              ref={setArrow as LegacyRef<HTMLDivElement>}
            />
          </div>
        )}
        offset={getOffset(orientation, isMobile) as [number, number]}
        popperOptions={{
          modifiers: [
            {
              name: 'arrow',
              options: {
                element: arrow,
              },
            },
          ],
        }}
      >
        <span
          onClick={() => {
            if (showOnClick) {
              onboardingTealiumService.onErrorToolTipActions(
                content[errorIndex].name,
                'click',
                pageName,
              );
              setVisible(true);
            }
          }}
        >
          {children}
        </span>
      </Tippy>
    );
  };

  return getTippy(showOnClick, isMobile);
};
